// src/data/selfRepresentation.js
//
// ONB-6b - THE §44 ACT. A member's written notice that he represents himself, as a dated,
// attributed, recorded act.
//
// THE GAP THIS CLOSES. Ruling 135 says the Organization "will always allow the membership to
// represent themselves and are encouraged and empowered through this platform". Constitution §44
// says the Organization acts for the member "except where the member involved serves reasonable
// written notice on the Organization to the contrary". The platform had no way to serve that
// notice. A notice telling a member he holds a right the platform gives him no way to exercise is
// worse than no notice - it is a promise the machine cannot keep.
//
// PURE. No Firestore, no network, no clock. The caller supplies the server time.

// §44, reproduced exactly (PD-16). The citation is the line of the QUOTED TEXT - the heading sits at
// 7549 and the page footer at 7565, and neither is quoted here.
export const SECTION_44_CITE = 'data/agreements/smart-constitution-2024.source.txt:7550-7564';
export const SECTION_44_HEADING = 'SECTION 44 - AUTHORITY TO REPRESENT';

// The clause the act turns on, verbatim from the source at the citation above.
export const SECTION_44_EXCEPT = 'except where the member involved serves reasonable written notice '
  + 'on the Organization to the contrary';

// What the member is told, in plain words, before he decides. It states the right AND the cost -
// ruling 204: a self-representing member gets the same rule matching as anyone, never an opinion,
// and ruling 201: nothing here writes his procedural objections for him.
export const NOTICE_VERSION = 'v1';
export const NOTICE = Object.freeze({
  v1: [
    'Under Section 44 of the SMART Constitution, your Union represents you and may dispose of your '
    + 'claim, unless you serve reasonable written notice on the Organization to the contrary.',
    'You may serve that notice here. If you do, you carry the claim yourself: you file it, you meet '
    + 'every time limit, and you answer the carrier. The platform will match the same agreement '
    + 'rules for you that it matches for any officer, and it will not give you an opinion, write '
    + 'your objections, or tell you what to argue.',
    'Your Local Chairperson and General Chairperson are told that you have served this notice. They '
    + 'do not disappear: the notice is served ON the Organization, and the Organization still holds '
    + 'the agreement. You may withdraw it at any time, and the withdrawal is recorded the same way.',
    'This platform records. It does not represent you, and AI can be wrong, including this app.',
  ].join('\n\n'),
});

export const ACTS = Object.freeze(['served', 'withdrawn']);

// WHO HOLDS THE CLAIM. The default is the Organization - that is what §44 says, and silence is never
// read as a notice. Only a SERVED act that has not been withdrawn moves carriage to the member.
export const ORGANIZATION = 'organization';
export const MEMBER = 'member';

export function noticeText(version) {
  return NOTICE[String(version || NOTICE_VERSION)] || '';
}

// An act is written, dated and attributed, or it is not an act. A notice nobody can date and nobody
// signed is exactly the thing §44 requires and the thing this module exists to produce.
export function validateAct({ act, actorUid, actorEmail, now, noticeVersion, statement }) {
  const problems = [];
  if (!ACTS.includes(String(act || ''))) problems.push('act must be one of: ' + ACTS.join(', '));
  if (!String(actorUid || '').trim()) problems.push('an act must name who performed it');
  if (!String(actorEmail || '').trim()) problems.push('an act must carry the address it was served from');
  if (!String(now || '').trim()) problems.push('an act must be dated');
  // The version is required on SERVING - it is what words he was shown - and meaningless on
  // withdrawal, which withdraws whatever was served.
  if (String(act) === 'served') {
    if (!noticeText(noticeVersion)) problems.push('the notice version shown is not one this build holds');
    if (!String(statement || '').trim()) problems.push('serving notice requires the member to state it in his own words (§44: "reasonable written notice")');
  }
  return { ok: problems.length === 0, problems };
}

// The record. Append-only by construction: withdrawing is a NEW act, never an edit of the old one,
// for the same reason an onboarding record cannot be rewritten - the question it answers is what was
// served, and when, and by whom.
export function buildAct({ claimId, act, actorUid, actorEmail, actorName, now, noticeVersion, statement }) {
  return {
    claimId: String(claimId || ''),
    act: String(act || ''),
    actorUid: String(actorUid || ''),
    actorEmail: String(actorEmail || ''),
    actorName: String(actorName || ''),
    servedAt: String(now || ''),
    // What he was shown, and what he wrote. Both, because §44 asks for HIS notice, not a tick.
    noticeVersion: String(act) === 'served' ? String(noticeVersion || '') : '',
    statement: String(act) === 'served' ? String(statement || '') : '',
    // The authority, carried on the record itself so a reader years later need not go looking.
    cite: SECTION_44_CITE,
  };
}

// THE STATE OF THE SECTION 44 NOTICE - NOT the carriage of the claim, and the difference is the
// reason this function was renamed on 2026-09-22 (section 363; it was `carriageOf` until then).
//
// What this answers: has this member served written notice on the Organization that he represents
// himself, and is that notice still standing? With NO acts the answer is the ORGANIZATION, because
// §44 gives it authority to represent "except where the member serves reasonable written notice to
// the contrary" - and SILENCE IS NEVER A NOTICE.
//
// What this does NOT answer, and must never be read as answering: who is carrying the claim. That
// is `carriageOf()` in src\data\carriage.js, whose default is the CLAIMANT (ruling 144). A member
// who has served no §44 notice still carries his own claim until he hands it over; the two
// questions have different right answers and had the same name until this was fixed.
//
// Latest act wins; ties are impossible on a server clock but are resolved by order of arrival so
// the function is total.
export function noticeStateOf(acts) {
  const list = (Array.isArray(acts) ? acts : []).filter((a) => a && ACTS.includes(String(a.act)));
  if (!list.length) return { authority: ORGANIZATION, since: '', act: null, notice: false };
  const sorted = list.slice().sort((a, b) => String(a.servedAt).localeCompare(String(b.servedAt)));
  const last = sorted[sorted.length - 1];
  const served = String(last.act) === 'served';
  return {
    authority: served ? MEMBER : ORGANIZATION,
    since: String(last.servedAt || ''),
    act: last,
    notice: served,
  };
}

// RULING 205 (assistant, 2026-09-22, under PD-19 - recorded by number and withdrawable).
//
// A served notice DOES NOT LOCK THE OFFICE OUT. It is surfaced, loudly, on the claim and on the
// desk, and the officer must acknowledge it before he works the claim.
//
// The argument: §44's notice is served ON the Organization, and the Organization still holds the
// agreement. A platform that silently stripped an officer's ability to act on a member's checkbox
// could orphan a claim inside a filing window that nobody is then watching - the member believing
// the Union has it, the Union locked out, the time limit running. That is the failure ruling 197
// already reasoned about for the Organization's own clock: the platform RECORDS and SURFACES, and
// does not intervene in a relationship it is not a party to.
//
// What an officer sees is therefore a blocking NOTICE, not a blocking RULE. He is told, by name and
// date, that this member has served written notice under §44; what he does next is his to answer
// for, as it would be on paper.
//
// THIS IS THE ONE MOST LIKELY TO WANT BOB'S OVERRIDE. If he wants the notice to hard-stop every
// officer write, that is one predicate on this function and the module is built to take it.
export function officeMayAct(acts) {
  const c = noticeStateOf(acts);
  if (!c.notice) return { may: true, mustAcknowledge: false, because: '' };
  return {
    may: true,
    mustAcknowledge: true,
    because: 'This member served written notice under Section 44 on ' + c.since + ' that he '
      + 'represents himself. The Organization is not locked out of this claim and the time limits '
      + 'have not stopped. Acknowledge that you have read this before working it.',
  };
}
